import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

/**
 * המשימות שצמודות לשיבוץ אחד (מיגרציה 049).
 *
 * שאילתה נפרדת ולא שדה ב schedule_week: המשימות נקראות רק כשפותחים
 * משמרת, והלוח נטען הרבה יותר פעמים ממה שפותחים אותו.
 */
export type ShiftTask = {
  id: string;
  title: string;
  requires_photo: boolean;
  completed_at: string | null;
  status: { name: string; is_done_state: boolean } | null;
};

const key = (assignmentId: string) => ['shift-tasks', assignmentId] as const;

export function useShiftTasks(assignmentId: string | null) {
  return useQuery({
    queryKey: key(assignmentId ?? 'none'),
    enabled: assignmentId !== null,
    queryFn: async (): Promise<ShiftTask[]> => {
      const { data, error } = await supabase
        .from('tasks')
        .select('id, title, requires_photo, completed_at, status:task_statuses(name, is_done_state)')
        .eq('assignment_id', assignmentId ?? '')
        .order('created_at');
      if (error) throw error;
      return (data ?? []) as unknown as ShiftTask[];
    },
  });
}

/**
 * כמה משימות יש לכל שיבוץ בשבוע.
 *
 * שאילתה אחת לכל הלוח ולא אחת לכל שיבוץ, כדי שהסימון בלוח לא
 * יעלה שישים קריאות. הסינון הוא לפי התאריך של השיבוץ עצמו,
 * דרך ה join, ולא לפי תאריך היעד של המשימה.
 */
export function useWeekTaskCounts(weekStart: string, weekEnd: string) {
  return useQuery({
    queryKey: ['week-task-counts', weekStart] as const,
    queryFn: async (): Promise<Map<string, number>> => {
      const { data, error } = await supabase
        .from('tasks')
        .select('assignment_id, shift_assignments!inner(date)')
        .gte('shift_assignments.date', weekStart)
        .lte('shift_assignments.date', weekEnd);
      if (error) throw error;

      const counts = new Map<string, number>();
      for (const row of (data ?? []) as { assignment_id: string | null }[]) {
        if (row.assignment_id === null) continue;
        counts.set(row.assignment_id, (counts.get(row.assignment_id) ?? 0) + 1);
      }
      return counts;
    },
  });
}

/**
 * תבניות משימה: ניסוחים שחוזרים, כדי שלא יוקלדו מחדש בכל משמרת.
 *
 * המסננים הם של התבנית ולא של השאילתה: תבנית בלי סניף מתאימה
 * לכולם, ותבנית של סניף מסוים מופיעה רק שם.
 */
export type TaskTemplate = {
  id: string;
  title: string;
  branch_id: string | null;
  job_title_id: string | null;
  requires_photo: boolean;
};

export function useTaskTemplates(branchId: string | null, jobTitleId: string | null) {
  return useQuery({
    queryKey: ['task-templates'] as const,
    queryFn: async (): Promise<TaskTemplate[]> => {
      const { data, error } = await supabase
        .from('task_templates')
        .select('id, title, branch_id, job_title_id, requires_photo')
        .eq('is_active', true)
        .order('sort_order')
        .order('title');
      if (error) throw error;
      return (data ?? []) as TaskTemplate[];
    },
    select: (rows) =>
      rows.filter(
        (row) =>
          (row.branch_id === null || row.branch_id === branchId) &&
          (row.job_title_id === null || row.job_title_id === jobTitleId),
      ),
  });
}

export function useShiftTaskMutations(assignmentId: string | null) {
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: key(assignmentId ?? 'none') });
    // גם הסימון בלוח, אחרת המשימה נוספה והאייקון לא הופיע
    void queryClient.invalidateQueries({ queryKey: ['week-task-counts'] });
  };

  const add = useMutation({
    mutationFn: async (input: { title: string; requires_photo: boolean }) => {
      const { error } = await supabase.rpc('add_shift_task', {
        p_assignment: assignmentId ?? '',
        p_title: input.title,
        p_requires_photo: input.requires_photo,
      });
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const addFromTemplate = useMutation({
    mutationFn: async (input: { template_id: string; requires_photo: boolean | null }) => {
      const { error } = await supabase.rpc('add_shift_task_from_template', {
        p_assignment: assignmentId ?? '',
        p_template: input.template_id,
        p_requires_photo: input.requires_photo ?? undefined,
      });
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const remove = useMutation({
    mutationFn: async (taskId: string) => {
      const { error } = await supabase.from('tasks').delete().eq('id', taskId);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  return { add, addFromTemplate, remove };
}
